import React from "react";
import { Star, Plus, Package } from "lucide-react";
import type { StorefrontProduct } from "@/types/storefront";
import { useCart } from "@/contexts/CartContext";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

interface ProductCardProps {
  product: StorefrontProduct;
  index?: number;
}

const ProductCard = React.forwardRef<HTMLDivElement, ProductCardProps>(
  ({ product, index = 0 }, ref) => {
    const { addItem } = useCart();
    const discount = product.originalPrice
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : 0;
    const bv = product.businessVolume && product.businessVolume > 0
      ? product.businessVolume
      : Math.max(1, Math.round(product.price * 0.1));

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05, duration: 0.4 }}
        className="group bg-card rounded-2xl border border-border/50 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
      >
        <Link to={`/product/${product.id}`} className="block">
          <div className="relative aspect-square overflow-hidden bg-white">
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center bg-muted text-muted-foreground border-b border-border/50">
                <Package className="w-8 h-8 opacity-40 mb-1" />
                <span className="text-[10px] font-medium opacity-60">No Image</span>
              </div>
            )}
            {discount > 0 && (
              <span className="absolute top-2 left-2 bg-accent text-accent-foreground text-xs font-bold px-2 py-1 rounded-lg">
                {discount}% OFF
              </span>
            )}
            {product.tags?.includes("Bestseller") && (
              <span className="absolute top-2 right-2 bg-primary text-primary-foreground text-xs font-bold px-2 py-1 rounded-lg">
                Bestseller
              </span>
            )}
          </div>
        </Link>
        <div className="p-3.5">
          <Link to={`/stores/${product.storeId}`} className="text-xs text-primary hover:underline mb-1 inline-block">{product.storeName}</Link>
          <Link to={`/product/${product.id}`}>
            <h3 className="font-heading font-semibold text-sm leading-tight line-clamp-2 mb-1.5 hover:text-primary transition-colors">
              {product.name}
            </h3>
          </Link>
          <p className="text-xs text-muted-foreground mb-2">{product.unit}</p>

          <div className="flex items-center gap-1 mb-2.5">
            <Star className="w-3.5 h-3.5 fill-warning text-warning" />
            <span className="text-xs font-semibold">{product.rating}</span>
            <span className="text-xs text-muted-foreground">({product.reviewCount})</span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="font-heading font-bold text-base">₹{product.price}</span>
              {product.originalPrice && (
                <span className="text-xs text-muted-foreground line-through">₹{product.originalPrice}</span>
              )}
              <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20" title="Business Volume Rewards">
                {bv} BV
              </span>
            </div>
            <button
              onClick={(e) => { e.preventDefault(); addItem(product); }}
              className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:opacity-90 transition-opacity active:scale-95"
              aria-label={`Add ${product.name} to cart`}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    );
  }
);

ProductCard.displayName = "ProductCard";

export default ProductCard;
